/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  AttachFile as AttachFileIcon,
  FileCopy as FileCopyIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function PtwGuideDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          pb: 2,
          pt: 3,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 6 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.2),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 32, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {t("ptwGuide.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("ptwGuide.subtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.1),
            },
          }}
          aria-label={t("ptwGuide.closeAria")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Overview Section */}
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <InfoIcon sx={{ color: "info.main", fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold" color="info.main">
              {t("ptwGuide.overviewTitle")}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t("ptwGuide.overviewText")}
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          {/* Section 1: Understanding the Form Layout */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="1"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section1Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <DescriptionIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section1Point1")}
                    secondary={t("ptwGuide.section1Point1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <GroupIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section1Point2")}
                    secondary={t("ptwGuide.section1Point2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssignmentIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section1Point3")}
                    secondary={t("ptwGuide.section1Point3Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AssessmentIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section1Point4")}
                    secondary={t("ptwGuide.section1Point4Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section1Point5")}
                    secondary={t("ptwGuide.section1Point5Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Step 1 - Work Permit Information */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="2"
                  size="small"
                  sx={{
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section2Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section2Step1")}
                    secondary={t("ptwGuide.section2Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section2Step2")}
                    secondary={t("ptwGuide.section2Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section2Step3")}
                    secondary={t("ptwGuide.section2Step3Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section2Step4")}
                    secondary={t("ptwGuide.section2Step4Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section2Step5")}
                    secondary={t("ptwGuide.section2Step5Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Step 2 - Other Resources (Optional) */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="3"
                  size="small"
                  sx={{
                    bgcolor: "info.main",
                    color: "info.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section3Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section3Step1")}
                    secondary={t("ptwGuide.section3Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AttachFileIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section3Step2")}
                    secondary={t("ptwGuide.section3Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AttachFileIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section3Step3")}
                    secondary={t("ptwGuide.section3Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("ptwGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("ptwGuide.section3ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Step 3 - Hazard Identification */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="4"
                  size="small"
                  sx={{
                    bgcolor: "warning.main",
                    color: "warning.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("ptwGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("ptwGuide.section4ProTip")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section4Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section4Step1")}
                    secondary={t("ptwGuide.section4Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section4Step2")}
                    secondary={t("ptwGuide.section4Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section4Step3")}
                    secondary={t("ptwGuide.section4Step3Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Step 4 - Risk Assessment & Precautions */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="5"
                  size="small"
                  sx={{
                    bgcolor: "error.main",
                    color: "error.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="error.main"
                  display="block"
                  gutterBottom
                >
                  ⚠️ {t("ptwGuide.important")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("ptwGuide.section5Important")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section5Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section5Step1")}
                    secondary={t("ptwGuide.section5Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section5Step2")}
                    secondary={t("ptwGuide.section5Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section5Step3")}
                    secondary={t("ptwGuide.section5Step3Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section5Step4")}
                    secondary={t("ptwGuide.section5Step4Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 6: Step 5 - Review & Submit */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="6"
                  size="small"
                  sx={{
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section6Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section6Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "secondary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section6Step1")}
                    secondary={t("ptwGuide.section6Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "secondary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section6Step2")}
                    secondary={t("ptwGuide.section6Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "secondary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section6Step3")}
                    secondary={t("ptwGuide.section6Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="success.main"
                  display="block"
                  gutterBottom
                >
                  ✓ {t("ptwGuide.onSuccess")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("ptwGuide.section6Success")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 7: Duplicating Permits */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="7"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section7Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section7Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <FileCopyIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section7Step1")}
                    secondary={t("ptwGuide.section7Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <FileCopyIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section7Step2")}
                    secondary={t("ptwGuide.section7Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <FileCopyIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section7Step3")}
                    secondary={t("ptwGuide.section7Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("ptwGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("ptwGuide.section7ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 8: Comments and Collaboration */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="8"
                  size="small"
                  sx={{
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section8Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("ptwGuide.section8Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CommentIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section8Step1")}
                    secondary={t("ptwGuide.section8Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CommentIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section8Step2")}
                    secondary={t("ptwGuide.section8Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CommentIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ptwGuide.section8Step3")}
                    secondary={t("ptwGuide.section8Step3Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 9: Best Practices */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="9"
                  size="small"
                  sx={{
                    bgcolor: "warning.main",
                    color: "warning.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("ptwGuide.section9Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("ptwGuide.section9Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("ptwGuide.section9Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("ptwGuide.section9Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("ptwGuide.section9Step4")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("ptwGuide.section9Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 3 }} />

          {/* Quick Reference */}
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.main"
              gutterBottom
            >
              📋 {t("ptwGuide.quickRefTitle")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("ptwGuide.quickRef1Title")}:</strong>{" "}
                      {t("ptwGuide.quickRef1Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("ptwGuide.quickRef2Title")}:</strong>{" "}
                      {t("ptwGuide.quickRef2Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("ptwGuide.quickRef3Title")}:</strong>{" "}
                      {t("ptwGuide.quickRef3Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("ptwGuide.quickRef4Title")}:</strong>{" "}
                      {t("ptwGuide.quickRef4Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("ptwGuide.quickRef5Title")}:</strong>{" "}
                      {t("ptwGuide.quickRef5Text")}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("ptwGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PtwGuideDialog;
